import { loadStripe } from '@stripe/stripe-js';

const API_BASE = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

class PaymentAPI {
  constructor() {
    this.stripePromise = loadStripe('pk_test_51I8UU0Cnn6VPZZJIPTrb05Um6tGgoW1FMzkjPbMRM9LUkxCD0J7lRehjkmjNKnWYVmOcW3OZmYuqXy4vjs8f5vwb00LJgXS4kv'); // This would come from env in production
  }

  getAuthHeaders() {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  async createCheckoutSession(paymentType, planId = null) {
    try {
      const baseUrl = window.location.origin;
      
      const response = await fetch(`${API_BASE}/api/payments/checkout/session`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          payment_type: paymentType,
          plan_id: planId,
          success_url: `${baseUrl}/profile/edit?tab=subscription&success=true`,
          cancel_url: `${baseUrl}/profile/edit?tab=subscription&canceled=true`,
          metadata: {
            source: 'subscription_page'
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  async redirectToCheckout(sessionId) {
    try {
      const stripe = await this.stripePromise;
      const { error } = await stripe.redirectToCheckout({ sessionId });
      
      if (error) {
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error redirecting to checkout:', error);
      throw error;
    }
  }

  async getPaymentStatus(sessionId) {
    try {
      const response = await fetch(`${API_BASE}/api/payments/status/${sessionId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting payment status:', error);
      throw error;
    }
  }

  async getPaymentHistory(limit = 10) {
    try {
      const response = await fetch(`${API_BASE}/api/payments/history?limit=${limit}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting payment history:', error);
      throw error;
    }
  }

  async getUserSubscription() {
    try {
      const response = await fetch(`${API_BASE}/api/subscription/my-subscription`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting subscription:', error);
      throw error;
    }
  }

  // Subscription management methods
  async pauseSubscription() {
    try {
      const response = await fetch(`${API_BASE}/api/payments/subscription/pause`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error pausing subscription:', error);
      throw error;
    }
  }

  async resumeSubscription() {
    try {
      const response = await fetch(`${API_BASE}/api/payments/subscription/resume`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error resuming subscription:', error);
      throw error;
    }
  }

  async cancelSubscription() {
    try {
      const response = await fetch(`${API_BASE}/api/payments/subscription/cancel`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  }

  async createCustomerPortalSession() {
    try {
      const response = await fetch(`${API_BASE}/api/payments/subscription/customer-portal`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating customer portal session:', error);
      throw error;
    }
  }

  // Payment type constants
  static PaymentTypes = {
    SUBSCRIPTION: 'subscription',
    GENEALOGY_FEE: 'genealogy_fee',
    ONE_TIME: 'one_time'
  };

  static PlanIds = {
    CROWN_PLAN: 'crown_plan'
  };
}

export default new PaymentAPI();